import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, type AdminTask } from '../api.ts';

/**
 * IN-HOUSE PRODUCTION — a `record` project's task board. "I can create
 * tasks… give it a title and describe the task and how many people it
 * requires." A task is STANDING: it accumulates recordings until the admin
 * archives it; the produced count on the card is the number of takes that
 * have landed under it so far.
 */
export function Tasks({
  token,
  projectId,
  onSignedOut,
}: {
  token: string;
  projectId: string;
  onSignedOut: () => void;
}): JSX.Element {
  const [tasks, setTasks] = useState<AdminTask[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [script, setScript] = useState('');
  const [people, setPeople] = useState<1 | 2>(1);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { tasks } = await api.listTasks(token, projectId);
      setTasks(tasks);
      setError(null);
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) return onSignedOut();
      setError(err instanceof ApiFailure ? err.message : 'Could not load the tasks.');
    }
  }, [token, projectId, onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(): Promise<void> {
    if (busy || !title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.createTask(token, projectId, {
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(script.trim() ? { script: script.trim() } : {}),
        peopleRequired: people,
      });
      setTitle('');
      setDescription('');
      setScript('');
      setCreating(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : 'Could not create the task.');
    } finally {
      setBusy(false);
    }
  }

  async function archive(task: AdminTask): Promise<void> {
    const ok = window.confirm(
      `Archive “${task.title}”?\n\nIt disappears from every phone. The ${task.producedCount} ` +
        'recording(s) it produced stay exactly where they are.',
    );
    if (!ok) return;
    try {
      await api.patchTask(token, task.id, { archived: true });
      await load();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : 'Could not archive it.');
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>In-house production</h2>
        <span className="topbar-spacer" />
        {!creating && (
          <button className="btn-primary" type="button" style={{ width: 'auto' }} onClick={() => setCreating(true)}>
            + Create task
          </button>
        )}
      </div>
      <p className="hint">
        A task appears on every member’s phone with a record badge. Solo tasks record one voice;
        duo tasks open a conversation between two people. Every take lands below as a recording.
      </p>

      {creating && (
        <div className="adduser">
          <div className="adduser-grid">
            <label className="field">
              <span className="field-label">Title</span>
              <input
                className="input"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ask for directions to the citadel"
                autoFocus
                disabled={busy}
              />
            </label>
            <label className="field">
              <span className="field-label">People</span>
              <select value={people} onChange={(e) => setPeople(Number(e.target.value) === 2 ? 2 : 1)} disabled={busy}>
                <option value={1}>1 — solo, record yourself</option>
                <option value={2}>2 — a conversation</option>
              </select>
            </label>
          </div>
          <label className="field">
            <span className="field-label">Description</span>
            <input
              className="input"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What to do, in a sentence or two"
              disabled={busy}
            />
          </label>
          <label className="field">
            <span className="field-label">Script (optional)</span>
            <input
              className="input"
              type="text"
              value={script}
              onChange={(e) => setScript(e.target.value)}
              placeholder="The words to perform, when the task comes with them"
              disabled={busy}
            />
          </label>
          <div className="btn-row">
            <button className="btn-quiet" type="button" disabled={busy} onClick={() => setCreating(false)}>
              Cancel
            </button>
            <button
              className="btn-primary"
              type="button"
              style={{ width: 'auto' }}
              disabled={busy || !title.trim()}
              onClick={() => void create()}
            >
              {busy ? 'Creating…' : 'Create task'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="notice notice-warn" role="status">
          {error}
        </div>
      )}

      {tasks === null ? (
        <p className="hint-tight">Loading…</p>
      ) : tasks.length === 0 && !creating ? (
        <p className="hint-tight">No tasks yet — create one and it appears on every phone.</p>
      ) : (
        tasks.map((t) => (
          <div className="card" key={t.id} style={{ marginTop: 10, background: 'var(--surface2)' }}>
            <div className="card-head">
              <h2>{t.title}</h2>
              <span className={`pill ${t.peopleRequired === 2 ? 'pill-processing' : 'pill-review'}`}>
                {t.peopleRequired === 2 ? 'duo' : 'solo'}
              </span>
              <span className="topbar-spacer" />
              <span className="hint-tight num" style={{ margin: 0 }}>
                {t.producedCount} take{t.producedCount === 1 ? '' : 's'}
              </span>
              <button className="btn-link" type="button" onClick={() => void archive(t)}>
                Archive
              </button>
            </div>
            {t.description && (
              <p className="hint" style={{ marginBottom: t.script ? 8 : 0 }}>
                {t.description}
              </p>
            )}
            {t.script && (
              <p className="hint" style={{ marginBottom: 0 }}>
                <strong>Script:</strong> {t.script}
              </p>
            )}
          </div>
        ))
      )}
    </section>
  );
}
